import { useCallback, useRef, useState } from 'react'
import { fetchVoiceToken, pruefeStichwort } from '../lib/api'
import { resumeAudioContext, suspendAudioContext } from '../lib/audioContext'
import { ElevenLabsVoiceClient, MicCapture, PcmAudioPlayer } from '../lib/voiceClient'
import { resetVoiceDiag, voiceDiag, voiceExperiments } from '../lib/voiceDiagnostics'
import {
  DazwischenAblauf,
  OPTIONEN_HINTER_FILTER,
  STANDARD_OPTIONEN,
  STIMMHAFT_AB,
  dazwischenOptionenAusAdresse,
} from '../lib/dazwischenreden'
import { DENKTON_NACH_MS, playThinkingTone } from '../lib/thinkingTone'
import { vibrate, VIBRATION_MIKRO } from '../lib/haptics'
import { useChatStore } from '../state/chatStore'

/**
 * So viel Aufnahme geht zur Stichwort-Prüfung: der Ansatz vor dem Verdacht und
 * das Wort selbst — ein „Nächster Case“ passt hinein, ein Satz nicht.
 */
const STICHWORT_AUFNAHME_SEKUNDEN = 1.5

/**
 * Nach der Begrüßung geht das Mikrofon einmal von selbst auf. Sagt der
 * Besucher dann nichts, schließt es sich wieder — im Lab steht das Gerät in
 * einem Raum mit Publikum, ein offenes Mikrofon ohne Absicht ist dort falsch.
 */
const AUTO_MIKRO_ZU_NACH_MS = 12000
/**
 * Kurze Pause zwischen zwei Sätzen der Begrüßung darf das Mikrofon nicht
 * öffnen: Erst wenn so lange nichts nachkommt, hat K4ia wirklich ausgeredet.
 */
const AUSGEREDET_NACH_MS = 700

export type VoiceSessionStatus = 'idle' | 'connecting' | 'active' | 'error'

/**
 * Zwei Arten zu sprechen:
 *
 * - **`gespraech`** (Handy, Sprachmodus im Vollbild): einmal starten, dann
 *   einfach reden — wie der Sprachmodus von ChatGPT. K4ia hört zu, antwortet,
 *   hört danach von selbst wieder zu. Während sie spricht, sendet das
 *   Mikrofon Stille: Die Echounterdrückung des Browsers ist aus (voiceClient.ts,
 *   sonst knackt ihre Stimme am iPhone), ein offenes Mikrofon hörte sie selbst
 *   und fiele sich ins Wort. Unterbrechen geht per Stimme — das Gerät hört
 *   hinter dem eigenen Echofilter mit (lib/echoFilter.ts, dazwischenreden.ts) —
 *   oder per Tippen auf K4ia. Sagt eine Weile
 *   niemand etwas, pausiert das Zuhören — im Lab steht das Handy auch mal
 *   offen auf dem Tisch.
 * - **`tippen`** (Kiosk): Tippen zum Sprechen, das Mikrofon geht zu, sobald
 *   K4ia antwortet. Am fest aufgestellten Gerät stehen oft mehrere Leute —
 *   dort soll nur zuhören, wer es ausdrücklich will.
 */
export type VoiceMode = 'gespraech' | 'tippen'

/** Im Gesprächsmodus: So lange ohne ein Wort, dann pausiert das Zuhören. */
const STILLE_PAUSE_MS = 45_000
/** Wie micLevel in voiceClient.ts: Effektivwert ×8 ergibt die Anzeige 0…1. */
const PEGEL_VERSTAERKUNG = 8
/** Pegel, ab dem das Mikrofon „jemand spricht" zählt (für die Pause bei Stille). */
const SPRACH_PEGEL = 0.08
/**
 * Nach dem Dazwischenreden so lange hinhören. War bis dahin kaum Stimme im
 * Mikrofon (und kein Satz erkannt), war es ihr eigenes Echo — die Erkennung
 * wird dann unempfindlicher. Wer länger redet, bekommt die Abschrift erst
 * später; das zählt nicht als Fehlalarm.
 */
const DAZWISCHEN_PRUEFEN_MS = 2500
/** Blöcke mit Stimme (~43 ms je Block), ab denen es sicher kein Fehlalarm war. */
const DAZWISCHEN_STIMME_BLOECKE = 8

/**
 * Wires the ElevenLabs WebSocket client (voiceClient.ts) into the shared
 * chat transcript and avatar state.
 *
 * FR-25: Das Mikrofon sendet ausschließlich zwischen `holdToTalk` (Tippen an)
 * und `releaseToTalk` (Tippen aus oder K4ia antwortet) — im
 * Lab steht das Gerät in einem Raum mit Publikum, und ein offenes Mikrofon
 * nimmt auf, was niemand an K4ia gerichtet hat.
 */
export function useVoiceSession(mode: VoiceMode = 'tippen') {
  const [status, setStatus] = useState<VoiceSessionStatus>('idle')
  /** Gesprächsmodus: Zuhören angehalten (Stumm-Knopf oder lange Stille). */
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(false)
  const letzteStimmeRef = useRef(0)
  const stilleIntervallRef = useRef<ReturnType<typeof setInterval> | null>(null)
  /**
   * **Gesprochen wird ausschließlich auf ausdrückliches Öffnen** — Tippen an,
   * Tippen aus, und sobald K4ia antwortet, geht das Mikrofon von selbst zu.
   *
   * Im Lab steht das Gerät in einem Raum mit Publikum: Ein dauerhaft offenes
   * Mikrofon nimmt Gespräche auf, die niemand an K4ia gerichtet hat, und man
   * sieht dem Gerät nicht an, wann es zuhört. So ist es eindeutig, auch für
   * die, die danebenstehen.
   *
   * Die Konstante bleibt als benannter Wert stehen, statt die Bedingungen unten
   * wegzustreichen: Sie macht im Code sichtbar, dass hier eine Entscheidung
   * getroffen wurde, und nicht, dass jemand einen Schalter vergessen hat.
   */
  const pushToTalk = true
  const gespraech = mode === 'gespraech'
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  /**
   * Gesprächsmodus: Unterbrechen per Stimme, wie bei ChatGPT. Die
   * Echounterdrückung des Browsers bleibt aus (iPhone-Knacken); stattdessen
   * zieht der eigene Echofilter ihre Stimme aus dem Mikrofon, und das Handy
   * hört lokal mit, ob jemand dazwischenredet — in zwei Stufen: erst wird sie
   * leiser, dann still (lib/dazwischenreden.ts). Gesendet wird dabei Stille. Mit `?aec=1`
   * zum Vergleich der andere Weg: Echounterdrückung an, Mikrofon offen, und
   * ElevenLabs erkennt das Dazwischenreden selbst.
   */
  // Einmal je Sitzung aus der Adresse — nicht bei jedem Neuzeichnen neu.
  const [aecAn] = useState(() => voiceExperiments().aecAn)
  const [dazwischenOptionen] = useState(() => gespraechOptionen(mode, aecAn))
  const dazwischenAn = dazwischenOptionen !== null || (mode === 'gespraech' && aecAn)
  const detektorRef = useRef<DazwischenAblauf | null>(null)
  const dazwischenPruefRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Blöcke mit Stimme seit dem letzten Dazwischenreden. */
  const dazwischenStimmeRef = useRef(0)

  const clientRef = useRef<ElevenLabsVoiceClient | null>(null)
  const micRef = useRef<MicCapture | null>(null)
  const playerRef = useRef<PcmAudioPlayer | null>(null)
  const currentTurnIdRef = useRef<string | null>(null)
  /** Hat die aktuelle Sprechblase schon Text — oder zeigt sie noch die Figur? */
  const turnHatTextRef = useRef(false)
  /** Ein Tipp statt zwei: Nach der Begrüßung öffnet sich das Mikrofon einmal von selbst. */
  const autoMikroRef = useRef<'wartet' | 'offen' | 'vorbei'>('vorbei')
  const autoMikroTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const ausgeredetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Denk-Ton: läuft, bis das erste Audiostück da ist (lib/thinkingTone.ts). */
  const denktonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const denktonStopRef = useRef<(() => void) | null>(null)
  const holdToTalkRef = useRef<() => void>(() => {})
  const releaseToTalkRef = useRef<() => void>(() => {})
  const zuhoerenRef = useRef<() => void>(() => {})
  const dazwischenRef = useRef<() => void>(() => {})
  const pauseRef = useRef<() => void>(() => {})

  function denktonPlanen() {
    denktonAus()
    denktonTimerRef.current = setTimeout(() => {
      denktonTimerRef.current = null
      denktonStopRef.current = playThinkingTone()
    }, DENKTON_NACH_MS)
  }
  function denktonAus() {
    if (denktonTimerRef.current) {
      clearTimeout(denktonTimerRef.current)
      denktonTimerRef.current = null
    }
    denktonStopRef.current?.()
    denktonStopRef.current = null
  }
  function autoMikroTimerAus() {
    if (autoMikroTimerRef.current) {
      clearTimeout(autoMikroTimerRef.current)
      autoMikroTimerRef.current = null
    }
    if (ausgeredetTimerRef.current) {
      clearTimeout(ausgeredetTimerRef.current)
      ausgeredetTimerRef.current = null
    }
  }

  function stilleAus() {
    if (stilleIntervallRef.current) {
      clearInterval(stilleIntervallRef.current)
      stilleIntervallRef.current = null
    }
  }

  const setAvatarState = useChatStore((s) => s.setAvatarState)
  const setMicLevel = useChatStore((s) => s.setMicLevel)
  const setMicOpen = useChatStore((s) => s.setMicOpen)
  const addUserTranscript = useChatStore((s) => s.addUserTranscript)
  const beginVoiceTurn = useChatStore((s) => s.beginVoiceTurn)
  const appendVoiceText = useChatStore((s) => s.appendVoiceText)
  const truncateVoiceText = useChatStore((s) => s.truncateVoiceText)
  /** Die zuletzt beendete Sprechblase — die Korrektur kommt erst nach der Unterbrechung. */
  const letzteBlaseRef = useRef<string | null>(null)
  const finalizeVoiceTurn = useChatStore((s) => s.finalizeVoiceTurn)

  const start = useCallback(async () => {
    setStatus('connecting')
    setErrorMessage(null)
    resetVoiceDiag()
    try {
      const { signedUrl, voiceToken } = await fetchVoiceToken()

      const client = new ElevenLabsVoiceClient()
      const player = new PcmAudioPlayer()
      const mic = new MicCapture()
      // Noch innerhalb der Berührung, die „Sprechen starten" ausgelöst hat:
      // Ein AudioContext, der später ohne Geste entsteht, bleibt auf dem
      // Telefon stumm.
      await player.prepare()
      clientRef.current = client
      playerRef.current = player

      client.on((event) => {
        switch (event.type) {
          case 'connected':
            setStatus('active')
            setAvatarState('listening')
            // Der Marker-Sender öffnet die Sprechblase mit der Figur selbst:
            // Das Transkript, das sie sonst öffnet, wird für Markierungen
            // unterdrückt — ohne das stünde drei Sekunden nichts da.
            useChatStore.getState().setVoiceMarkerSender((marker) => {
              if (currentTurnIdRef.current) finalizeVoiceTurn(currentTurnIdRef.current)
              currentTurnIdRef.current = beginVoiceTurn()
              turnHatTextRef.current = false
              setAvatarState('thinking')
              denktonPlanen()
              client.sendUserMessage(marker)
            })
            // Einstieg per NFC-Tag: Jetzt, verbunden, spricht K4ia gleich über
            // den gescannten Showcase — statt sich vorzustellen.
            void useChatStore.getState().flushVoiceEntry('voice')
            break
          case 'disconnected':
            useChatStore.getState().setVoiceMarkerSender(null)
            denktonAus()
            setStatus('idle')
            setAvatarState('idle')
            break
          case 'user_transcript':
            // Nach einem Dazwischenreden: Es kam ein Satz, es war richtig.
            if (dazwischenPruefRef.current) {
              clearTimeout(dazwischenPruefRef.current)
              dazwischenPruefRef.current = null
              detektorRef.current?.erkennung.bestaetigt()
            }
            player.verwerfen = false
            // Der Besucher hat etwas gesagt — das automatisch geöffnete
            // Mikrofon war nicht umsonst; ab hier wie gewohnt.
            autoMikroRef.current = 'vorbei'
            autoMikroTimerAus()
            addUserTranscript(event.text)
            setAvatarState('thinking')
            denktonPlanen()
            // Sofort eine leere Sprechblase öffnen: Sie zeigt die fliegende
            // Figur, bis der Text kommt. Ohne das stand nach dem eigenen Satz
            // drei Sekunden lang gar nichts auf dem Bildschirm — und drei
            // Sekunden Nichts fühlen sich an wie „geht nicht".
            if (currentTurnIdRef.current) finalizeVoiceTurn(currentTurnIdRef.current)
            currentTurnIdRef.current = beginVoiceTurn()
            turnHatTextRef.current = false
            break
          case 'agent_text':
            // ElevenLabs schickt je Beitrag **eine** vollständige Antwort, kein
            // Stückwerk. Sie landet in der beim Transkript geöffneten Blase;
            // gibt es keine oder hat die schon Text (Begrüßung, zweiter
            // Beitrag), bekommt sie eine eigene — vorher hing die zweite
            // Antwort ohne Absatz an der ersten.
            if (!currentTurnIdRef.current || turnHatTextRef.current) {
              if (currentTurnIdRef.current) finalizeVoiceTurn(currentTurnIdRef.current)
              currentTurnIdRef.current = beginVoiceTurn()
            }
            turnHatTextRef.current = true
            appendVoiceText(currentTurnIdRef.current, event.text)
            // Hat K4ia gerade ein Werkzeug offen gelassen (Kontaktkarte)? Das
            // Transkript sagt es nicht — der Server-Verlauf schon. Die Karte
            // hängt sich dann an diese Sprechblase.
            void useChatStore.getState().syncPendingConfirmation()
            break
          // Muss vor dem ersten Audiostück kommen — danach stünde der Context
          // schon mit der falschen Rate.
          case 'audio_format':
            player.setFormat(event.agentOutput)
            break
          case 'agent_audio_chunk':
            // Rest eines Satzes, den der Besucher schon abgebrochen hat — siehe
            // PcmAudioPlayer.verwerfen.
            if (player.verwerfen) break
            denktonAus()
            // Noch ein Stück Begrüßung: Das Mikrofon wartet weiter (oder geht
            // wieder zu, falls eine Satzpause es schon geöffnet hatte).
            if (ausgeredetTimerRef.current) {
              clearTimeout(ausgeredetTimerRef.current)
              ausgeredetTimerRef.current = null
            }
            if (autoMikroRef.current === 'offen') {
              autoMikroRef.current = 'wartet'
              if (autoMikroTimerRef.current) {
                clearTimeout(autoMikroTimerRef.current)
                autoMikroTimerRef.current = null
              }
            }
            // Sobald sie antwortet, geht das Mikrofon zu: Niemand muss daran
            // denken, und ihre Stimme läuft nicht zurück ins Mikrofon.
            // Mit Echounterdrückung (`?aec=1`) bleibt es im Gesprächsmodus offen —
            // dort unterbricht ElevenLabs selbst, wenn jemand spricht.
            if (mic.muted === false && !(gespraech && aecAn)) {
              mic.muted = true
              voiceDiag.gehalten = false
              setMicOpen(false)
              detektorRef.current?.neuerBeitrag()
            }
            setAvatarState('speaking')
            player.enqueue(event.audioBase64)
            break
          case 'interruption':
            player.verwerfen = false
            denktonAus()
            player.stopAll()
            if (currentTurnIdRef.current) {
              letzteBlaseRef.current = currentTurnIdRef.current
              finalizeVoiceTurn(currentTurnIdRef.current)
              currentTurnIdRef.current = null
            }
            setAvatarState('listening')
            break
          case 'agent_text_correction': {
            // Der Chat soll zeigen, was zu hören war — nicht den ganzen Satz,
            // den K4ia vorhatte. Die Blase wurde bei der Unterbrechung schon
            // geschlossen; die Korrektur gehört zu ihr.
            const id = currentTurnIdRef.current ?? letzteBlaseRef.current
            if (id) truncateVoiceText(id, event.text)
            break
          }
          case 'error':
            setStatus('error')
            setErrorMessage(event.message)
            break
        }
      })

      // Läuft schon ein Gespräch (der Besucher hat bereits etwas gesagt oder
      // geschrieben), soll K4ia sich nicht noch einmal vorstellen. Leer heißt:
      // keine Begrüßung, sie wartet auf den Besucher.
      // Ebenso beim Einstieg per Tag: Der Showcase ist die Begrüßung.
      const gespraechLaeuft =
        useChatStore.getState().messages.some((m) => m.role === 'user') ||
        useChatStore.getState().pendingVoiceEntry !== null
      client.connect(signedUrl, voiceToken, gespraechLaeuft ? { firstMessage: '' } : {})

      // Ein Tipp statt zwei: Stellt K4ia sich vor, geht das Mikrofon danach
      // einmal von selbst auf — der Besucher muss nicht erst den zweiten Knopf
      // finden. Ohne Begrüßung (Gespräch läuft schon, Einstieg per Tag) bleibt
      // es beim Tippen.
      autoMikroRef.current = gespraechLaeuft ? 'vorbei' : 'wartet'
      player.onIdle = () => {
        if (gespraech) {
          // Ausgeredet: wieder zuhören — außer der Besucher hat pausiert.
          if (pausedRef.current || ausgeredetTimerRef.current) return
          ausgeredetTimerRef.current = setTimeout(() => {
            ausgeredetTimerRef.current = null
            if (pausedRef.current || !micRef.current?.muted) return
            zuhoerenRef.current()
          }, AUSGEREDET_NACH_MS)
          return
        }
        if (autoMikroRef.current !== 'wartet' || ausgeredetTimerRef.current) return
        ausgeredetTimerRef.current = setTimeout(() => {
          ausgeredetTimerRef.current = null
          if (autoMikroRef.current !== 'wartet') return
          autoMikroRef.current = 'offen'
          holdToTalkRef.current()
          autoMikroTimerRef.current = setTimeout(() => {
            // Nichts gesagt: wieder zu. Ein offenes Mikrofon ohne Absicht ist
            // im Lab falsch (FR-25).
            if (autoMikroRef.current === 'offen') {
              autoMikroRef.current = 'vorbei'
              releaseToTalkRef.current()
            }
          }, AUTO_MIKRO_ZU_NACH_MS)
        }, AUSGEREDET_NACH_MS)
      }

      // Stumm, bis der Besucher den Knopf tippt. Stumm heißt dabei: Stille
      // senden, nicht nichts senden — siehe MicCapture.muted, warum das den
      // Unterschied zwischen „geht" und „geht nicht" macht.
      mic.muted = pushToTalk
      micRef.current = mic
      await mic.start(
        (base64) => client.sendAudioChunk(base64),
        (level) => {
          if (level > SPRACH_PEGEL) {
            letzteStimmeRef.current = Date.now()
            if (dazwischenPruefRef.current) dazwischenStimmeRef.current++
          }
        },
        // Ihre Stimme als Referenz für den Echofilter — nur, wenn per Stimme
        // unterbrochen werden soll.
        dazwischenOptionen ? player.referenz() : null,
      )
      // Die Pegelanzeige bei offenem Mikrofon. Während sie spricht, setzt sie
      // die Erkennung unten — nur mit dem, was über ihrem Echo liegt, sonst
      // gingen die Balken mit ihrer eigenen Stimme mit.
      mic.onHoeren = (level) => {
        if (mic.muted && detektorRef.current) return
        setMicLevel(!pausedRef.current && !mic.muted ? level : 0)
      }
      if (dazwischenOptionen) {
        // Mit Echofilter kommt fast nur noch der Besucher an — dafür gelten
        // andere Schwellen als fürs rohe Mikrofon (lib/dazwischenreden.ts).
        const ablauf = new DazwischenAblauf(
          dazwischenOptionenAusAdresse(
            mic.echoUnterdrueckt ? OPTIONEN_HINTER_FILTER : STANDARD_OPTIONEN,
          ) ?? STANDARD_OPTIONEN,
        )
        detektorRef.current = ablauf
        const entwarnen = () => {
          player.ducken(false)
          mic.echoLernenAnhalten(false)
          useChatStore.getState().setHoertDich(false)
        }
        const unterbrechen = () => {
          mic.echoLernenAnhalten(false)
          useChatStore.getState().setHoertDich(false)
          dazwischenRef.current()
        }
        mic.onRoh = (rms, restEcho, stimmhaft) => {
          if (pausedRef.current) return
          const zustand = useChatStore.getState().avatarState
          if (!mic.muted) {
            // Offenes Mikrofon, sie schweigt: So klingt der Besucher hier.
            if (zustand === 'listening') ablauf.erkennung.stimmeGehoert(rms)
            return
          }
          if (zustand !== 'speaking') {
            setMicLevel(0)
            if (ablauf.imVerdacht) {
              ablauf.neuerBeitrag()
              entwarnen()
            }
            return
          }
          // Mit Echofilter: gegen den Rest ihres Echos, den er schätzt. Ohne:
          // gegen ihren Pegel, der Anteil wird gelernt.
          // Der Filter lernt ihr Echo noch (erste Sekunden des Gesprächs):
          // nichts auswerten, sonst hielte die Erkennung ihr Echo für Stimme.
          if (restEcho === Number.POSITIVE_INFINITY) {
            setMicLevel(0)
            // Hat er gerade neu angefangen (Notausgang), darf sie nicht leise bleiben.
            if (ablauf.imVerdacht) {
              ablauf.neuerBeitrag()
              entwarnen()
            }
            return
          }
          const schritt = ablauf.update(rms, restEcho ?? player.pegel(), stimmhaft)
          voiceDiag.dazwischen = ablauf.erkennung.letzte
          // Die Balken zeigen, was sie vom Besucher hört — nicht ihr Echo.
          setMicLevel(
            rms > ablauf.erkennung.letzte.schwelle && stimmhaft >= STIMMHAFT_AB
              ? Math.min(1, rms * PEGEL_VERSTAERKUNG)
              : 0,
          )
          if (schritt === 'verdacht') {
            // Erste Stufe: still prüfen. Hörbar wird erst `deutlich` — sonst
            // wurde sie bei jedem Geräusch leiser (30.09.).
            voiceDiag.dazwischenVerdacht++
            mic.echoLernenAnhalten(true)
          } else if (schritt === 'deutlich') {
            // Seit ~½ s eindeutig Stimme: leiser werden und zeigen, dass sie zuhört.
            player.ducken(true)
            useChatStore.getState().setHoertDich(true)
          } else if (schritt === 'entwarnung') {
            entwarnen()
          } else if (schritt === 'bestaetigt') {
            unterbrechen()
          } else if (schritt === 'kurz') {
            // Kurz gesprochen und schon wieder still: War es „Stopp“ oder
            // „Nächster“? Solange die Prüfung läuft, bleibt sie leiser.
            const aufnahme = mic.vorlaufWav(STICHWORT_AUFNAHME_SEKUNDEN)
            voiceDiag.stichwortGeprueft++
            if (!aufnahme) {
              if (ablauf.stichwort(false) === 'entwarnung') entwarnen()
              return
            }
            void pruefeStichwort(aufnahme, useChatStore.getState().locale).then((treffer) => {
              if (treffer) voiceDiag.stichwort = treffer
              const folge = ablauf.stichwort(treffer !== null)
              if (folge === 'bestaetigt') unterbrechen()
              else if (folge === 'entwarnung') entwarnen()
            })
          }
        }
      }
      if (gespraech) {
        // Ohne Begrüßung sofort zuhören; mit Begrüßung öffnet onIdle danach.
        if (gespraechLaeuft) zuhoerenRef.current()
        letzteStimmeRef.current = Date.now()
        stilleIntervallRef.current = setInterval(() => {
          const m = micRef.current
          if (!m || m.muted || pausedRef.current) return
          if (Date.now() - letzteStimmeRef.current > STILLE_PAUSE_MS) pauseRef.current()
        }, 5000)
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage(
        err instanceof Error ? err.message : 'Mikrofon oder Sprachverbindung nicht verfügbar.',
      )
      // Kein Sprachmodus, aber ein gescannter Tag: dann eben im Chat — der
      // Besucher steht vor dem Stand und wartet.
      void useChatStore.getState().flushVoiceEntry('chat')
    }
  }, [
    gespraech,
    aecAn,
    dazwischenOptionen,
    setMicLevel,
    setMicOpen,
    pushToTalk,
    setAvatarState,
    addUserTranscript,
    beginVoiceTurn,
    appendVoiceText,
    truncateVoiceText,
    finalizeVoiceTurn,
  ])

  const stop = useCallback(() => {
    if (dazwischenPruefRef.current) {
      clearTimeout(dazwischenPruefRef.current)
      dazwischenPruefRef.current = null
    }
    detektorRef.current = null
    useChatStore.getState().setHoertDich(false)
    setMicLevel(0)
    denktonAus()
    autoMikroTimerAus()
    stilleAus()
    pausedRef.current = false
    setPaused(false)
    autoMikroRef.current = 'vorbei'
    if (currentTurnIdRef.current) {
      finalizeVoiceTurn(currentTurnIdRef.current)
      currentTurnIdRef.current = null
    }
    useChatStore.getState().setVoiceMarkerSender(null)
    setMicOpen(false)
    micRef.current?.stop()
    playerRef.current?.stopAll()
    clientRef.current?.disconnect()
    // Anhalten, nicht schließen — siehe lib/audioContext.ts.
    suspendAudioContext()
    micRef.current = null
    playerRef.current = null
    clientRef.current = null
    setStatus('idle')
    setAvatarState('idle')
  }, [setMicLevel, finalizeVoiceTurn, setAvatarState, setMicOpen])

  const holdToTalk = useCallback(() => {
    // Der Druck auf den Knopf heißt: Ich will etwas sagen. Spricht K4ia
    // gerade, verstummt sie sofort — nicht erst, wenn der Server nach einer
    // halben Sekunde Stimme erkannt hat. Der Rest ihres Satzes wird
    // verworfen, bis ein neuer Beitrag beginnt.
    if (playerRef.current) {
      playerRef.current.stopAll()
      playerRef.current.verwerfen = true
    }
    if (currentTurnIdRef.current) {
      letzteBlaseRef.current = currentTurnIdRef.current
      finalizeVoiceTurn(currentTurnIdRef.current)
      currentTurnIdRef.current = null
    }
    if (micRef.current) {
      micRef.current.muted = false
      // Was stumm mitgehört wurde, gehört nicht zu diesem Beitrag — außer
      // beim Dazwischenreden, das es vorher selbst hinterherschickt.
      micRef.current.vorlaufLeeren()
    }
    voiceDiag.gehalten = true
    voiceDiag.vadMax = 0
    denktonAus()
    vibrate(VIBRATION_MIKRO)
    setMicOpen(true)
    // Jeder Druck ist eine Geste — die günstigste Gelegenheit, einen vom
    // Telefon angehaltenen Context wieder in Gang zu bringen (Bildschirmsperre,
    // Anruf, Tabwechsel halten ihn an, ohne dass es jemand merkt).
    void resumeAudioContext()
    setAvatarState('listening')
  }, [setAvatarState, finalizeVoiceTurn, setMicOpen])

  const releaseToTalk = useCallback(() => {
    if (micRef.current) micRef.current.muted = true
    voiceDiag.gehalten = false
    setMicOpen(false)
  }, [setMicOpen])

  /** Gesprächsmodus: wieder zuhören (nach ihrer Antwort, nach der Pause). */
  const zuhoeren = useCallback(() => {
    pausedRef.current = false
    setPaused(false)
    letzteStimmeRef.current = Date.now()
    holdToTalk()
  }, [holdToTalk])

  /** Gesprächsmodus: Zuhören anhalten, bis der Besucher weitermacht. */
  const pause = useCallback(() => {
    pausedRef.current = true
    setPaused(true)
    if (ausgeredetTimerRef.current) {
      clearTimeout(ausgeredetTimerRef.current)
      ausgeredetTimerRef.current = null
    }
    releaseToTalk()
    setMicLevel(0)
    // Spricht sie gerade, darf sie ausreden; sonst ruht sie.
    if (useChatStore.getState().avatarState === 'listening') setAvatarState('idle')
  }, [releaseToTalk, setMicLevel, setAvatarState])

  /**
   * Gesprächsmodus: Der Besucher fällt ihr ins Wort. Sofort still, die letzte
   * Sekunde seiner Stimme hinterherschicken, zuhören. Kommt danach kein
   * Satz, war es ein Fehlalarm, und die Erkennung wird unempfindlicher.
   */
  const dazwischen = useCallback(() => {
    const mic = micRef.current
    if (!mic) return
    voiceDiag.dazwischenGezaehlt++
    mic.vorlaufSenden()
    zuhoerenRef.current()
    if (dazwischenPruefRef.current) clearTimeout(dazwischenPruefRef.current)
    dazwischenStimmeRef.current = 0
    dazwischenPruefRef.current = setTimeout(() => {
      dazwischenPruefRef.current = null
      if (dazwischenStimmeRef.current >= DAZWISCHEN_STIMME_BLOECKE) {
        // Da spricht wirklich jemand — die Abschrift kommt, wenn er fertig ist.
        detektorRef.current?.erkennung.bestaetigt()
        return
      }
      voiceDiag.dazwischenFehlalarme++
      detektorRef.current?.erkennung.fehlalarm()
    }, DAZWISCHEN_PRUEFEN_MS)
  }, [])

  holdToTalkRef.current = holdToTalk
  releaseToTalkRef.current = releaseToTalk
  dazwischenRef.current = dazwischen
  zuhoerenRef.current = zuhoeren
  pauseRef.current = pause

  return {
    status,
    errorMessage,
    paused,
    start,
    stop,
    holdToTalk,
    releaseToTalk,
    /** Gesprächsmodus: unterbrechen oder nach der Pause weitersprechen. */
    zuhoeren,
    pause,
    /** Gesprächsmodus: Unterbrechen per Stimme ist an. */
    dazwischenAn,
  }
}

/** Dazwischenreden per lokaler Erkennung: nur im Gesprächsmodus und ohne `?aec=1`. */
function gespraechOptionen(mode: VoiceMode, aecAn: boolean) {
  if (mode !== 'gespraech' || aecAn) return null
  return dazwischenOptionenAusAdresse()
}
